import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import FormatterInstance from '../../i18n/formatter';
import theme from '../../styled-components/dark';
import { CardModes } from '../../types/common';
import type { CardMode } from '../../types/common';
import { OutcomeStatuses, type OutcomeStatus } from '../../types/history';
import { getOutcomeColorFromStatus, getOutcomeIconFromStatus } from '../../utils/outcome';

type WrapperPropsType = {
  $alignRight: boolean;
};
const Wrapper = styled.div<WrapperPropsType>`
  display: flex;
  width: 100%;
  justify-content: space-between;

  ${({ $alignRight }) => css`
    ${$alignRight
      ? css`
          justify-content: flex-end;
        `
      : undefined}
  `}
`;

const OutcomeWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.34em;
`;

const StatusIconWrapper = styled.div`
  align-self: flex-start;
  margin-top: 0.1em;
`;

const StatusIcon = styled.div`
  width: 0.93em;
  height: 0.93em;
  overflow: visible !important;
`;

const FullTitle = styled.div``;

const Title = styled.span`
  font-size: 0.87em;
  color: ${theme.History.outcome.defaultContent.titleColor};
  margin-right: 0.34em;
`;

type LabelPropsType = {
  $color?: string;
};
const Label = styled.span<LabelPropsType>`
  font-weight: bold;

  ${({ $color }) => css`
    color: ${$color ?? theme.History.outcome.defaultContent.color};
  `}
`;

const HeaderRightSide = styled.div`
  display: flex;
  align-items: center;
`;

const OddsWrapper = styled.div`
  display: flex;

  align-items: flex-start;
`;

type OddsPropsType = {
  $isInitial?: boolean;
};

const Odds = styled.div<OddsPropsType>`
  font-size: 1em;
  font-weight: bold;
  border-radius: 0.4em;
  padding: 0.14em 0.33em;
  margin-left: 0.33em;

  ${({ $isInitial }) =>
    $isInitial
      ? css`
          text-decoration: line-through;
          color: ${theme.History.outcome.content.initialOddColor};
          background: ${theme.History.outcome.content.initialOddBackgroundColor};
        `
      : css`
          color: ${theme.History.outcome.content.oddColor};
          background: ${theme.History.outcome.content.oddBackgroundColor};
        `}
`;

type HistoryItemOutcomeDefaultContentProps = {
  className?: string;
  betTitle?: string;
  outcomeLabel: string;
  outcomeStatus: OutcomeStatus;
  cardMode?: CardMode;
  odds?: number;
  initialOdds?: number;
};

const HistoryItemOutcomeDefaultContent = ({
  className,
  betTitle,
  outcomeLabel,
  outcomeStatus,
  cardMode = CardModes.NORMAL,
  odds,
  initialOdds,
}: HistoryItemOutcomeDefaultContentProps) => {
  const isBoosted = useMemo(
    () => cardMode === CardModes.BOOSTED || cardMode === CardModes.BIG_BOOSTED || cardMode === CardModes.XMAS_BOOSTED,
    [cardMode],
  );
  const color = useMemo(() => {
    if (outcomeStatus !== OutcomeStatuses.RUNNING) {
      return getOutcomeColorFromStatus(outcomeStatus);
    }
  }, [outcomeStatus]);
  const title = useMemo(() => (cardMode === CardModes.NORMAL ? betTitle : null), [betTitle, cardMode]);
  const shouldDisplayBetTitle = useMemo(
    () =>
      !!~(
        [CardModes.NORMAL, CardModes.BOOSTED, CardModes.BIG_BOOSTED, CardModes.XMAS_BOOSTED] as Array<CardMode>
      ).indexOf(cardMode),
    [cardMode],
  );

  const Icon = useMemo(() => getOutcomeIconFromStatus(outcomeStatus), [outcomeStatus]);

  return (
    <Wrapper className={className} $alignRight={!!odds && !shouldDisplayBetTitle}>
      {shouldDisplayBetTitle ? (
        <OutcomeWrapper>
          <StatusIconWrapper>
            <StatusIcon as={Icon} />
          </StatusIconWrapper>
          <FullTitle>
            {!isBoosted ? <Title>{title}</Title> : null}
            <Label $color={color}>{outcomeLabel}</Label>
          </FullTitle>
        </OutcomeWrapper>
      ) : null}
      <HeaderRightSide>
        {odds ? (
          <OddsWrapper>
            {initialOdds ? <Odds $isInitial>{FormatterInstance.formatOdd(initialOdds)}</Odds> : null}
            <Odds>{FormatterInstance.formatOdd(odds)}</Odds>
          </OddsWrapper>
        ) : null}
      </HeaderRightSide>
    </Wrapper>
  );
};

export default HistoryItemOutcomeDefaultContent;
